import { useForm, UseFormReturn } from "react-hook-form";
import lodashCloneDeep from "lodash/cloneDeep";
import { FinancialTarget, TargetGroupWithTargets } from "components/product-collection/footer/types";
import { MetricsKind } from "helpers/metrics/types";
import { ISchemaKey } from "helpers/product-collection";
import uuid from "uuid/v4";
import { AllocationDimensionKind, IAllocationDimension } from "helpers/allocation-dimension";
import { ALLOCATION_DIMENSION_ID_PREFIX } from "constants/product-collection";

export enum FormFieldName {
    Name = "name",
    Metric = "metric",
    Type = "type",
    Values = "values",
    SchemaFields = "schema_fields",
}

export const ASSORTMENT_TARGET_TYPE = "assortment";

export interface FormFields {
    [FormFieldName.Name]: string | undefined;
    [FormFieldName.Metric]: string | undefined;
    [FormFieldName.Type]: string | undefined;
    // TODO: Target. React form complaints when typing with string[]
    [FormFieldName.Values]: any;
    // Dynamic field(s)
    [FormFieldName.SchemaFields]: {
        [fieldName: string]: string | undefined;
    };
}

/**
 * Helper function to extract field name.
 * It is assumed that each target will same fields each filter. So we'll only take it from one target.
 * This uses last item in the filter. Filters are stored in the order of dependency list, grand parent -> parent -> child
 * @param target
 * @returns
 */
const extractFieldNameFromTarget = (target: FinancialTarget): string => {
    return target.filters[target.filters.length - 1].field_name;
};

/**
 * Helper function to extract field name.
 * This uses last item in the filter. Filters are stored in the order of dependency list, grand parent -> parent -> child
 * @param target
 * @returns
 */
const extractTargetValuesFromTargets = (targets: FinancialTarget[]): string[] => {
    return targets
        .map((target) => {
            return target.filters[target.filters.length - 1]?.value;
        })
        .filter((value) => !!value);
};

/**
 * Helper function to extract parent fields and their values.
 * It is assumed that each target will have same length of filters with (IMPORTANT) consistent parents' values and (IMPORTANT) different child value.
 * Therefore, this function only uses first target
 * @param targets
 * @returns
 */
const extractParentFilterValuesFromTarget = (
    targets: FinancialTarget[]
): { [schemaField: string]: string | undefined } => {
    return targets[0].filters.reduce((acc, filter, index) => {
        if (index < targets[0].filters.length - 1) {
            acc[filter.field_name] = filter.value;
        }

        return acc;
    }, {});
};

export const makeEmptyTargetGroupWithTargets = (): TargetGroupWithTargets => {
    return {
        id: uuid(),
        name: "",
        metric: "" as MetricsKind,
        targets: [],
    };
};

/**
 * Convert Form values into Target Group and Targets snapshot
 * @param formValues
 * @param snapshot
 * @param allocationDimensions
 * @param dependencyFields
 * @returns
 */
const convertFormValuesToSnapshot = (
    formValues: FormFields,
    snapshot: TargetGroupWithTargets,
    allocationDimensions: IAllocationDimension[],
    dependencyFields: ISchemaKey[] = []
) => {
    if (!snapshot) {
        return;
    }

    const mutatedSnapshot = lodashCloneDeep(snapshot);
    mutatedSnapshot.name = formValues[FormFieldName.Name];
    mutatedSnapshot.metric = formValues[FormFieldName.Metric] as MetricsKind;

    const targetDimensions = allocationDimensions.reduce((acc, allocationDimension) => {
        // Making sure this is generic dimension
        if (allocationDimension.subtype === AllocationDimensionKind.Generic) {
            acc.push({
                id: allocationDimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, ""),
            });
        }

        return acc;
    }, []);

    if (formValues[FormFieldName.Type] === ASSORTMENT_TARGET_TYPE) {
        const existingTarget = mutatedSnapshot.targets.find((target) => target.filters.length === 0);
        if (!existingTarget) {
            mutatedSnapshot.targets = [
                {
                    id: uuid(),
                    name: "",
                    dimensions: targetDimensions,
                    target_group_id: mutatedSnapshot.id,
                    created_at_ms: new Date().getTime(),
                    modified_at_ms: new Date().getTime(),
                    filters: [],
                },
            ];
        }
    } else {
        // Clear targets first
        mutatedSnapshot.targets = [];

        formValues[FormFieldName.Values].forEach((targetFieldValue) => {
            // IMPORTANT: Notice that we search `snapshot` instead of `mutatedSnapshot`
            let target: FinancialTarget = snapshot.targets.find((target) => {
                const lastFilter = target.filters[target.filters.length - 1];
                const typeValueMatch =
                    lastFilter?.field_name === formValues[FormFieldName.Type] && lastFilter?.value === targetFieldValue;

                if (typeValueMatch) {
                    // This is recursive loop operation. Only do this when first criteria passes
                    const dependencyFieldMatch = dependencyFields.every((dependencyField) => {
                        const value = formValues[FormFieldName.SchemaFields][dependencyField.field_name];
                        const matchingFilter = target.filters.find(
                            (filter) => filter.field_name === dependencyField.field_name && filter.value === value
                        );

                        return !!matchingFilter;
                    });

                    return dependencyFieldMatch;
                }

                return false;
            });

            if (!target) {
                target = {
                    id: uuid(),
                    name: "",
                    dimensions: targetDimensions,
                    target_group_id: mutatedSnapshot.id,
                    created_at_ms: new Date().getTime(),
                    modified_at_ms: new Date().getTime(),
                    filters: dependencyFields.map((dependencyField) => ({
                        field_name: dependencyField.field_name,
                        value: formValues[FormFieldName.SchemaFields][dependencyField.field_name],
                    })),
                };

                // Final thing is to push target field itself as last item in filter
                target.filters.push({
                    field_name: formValues[FormFieldName.Type],
                    value: targetFieldValue,
                });
            }

            mutatedSnapshot.targets.push(target);
        });
    }

    return mutatedSnapshot;
};

function useTargetFormToSnapshot(
    form: UseFormReturn<FormFields>,
    originalSnapshot: TargetGroupWithTargets,
    allocationDimensions: IAllocationDimension[],
    dependencyFields: ISchemaKey[] = []
) {
    const updateSnapshot = () => {
        const { getValues } = form;
        return convertFormValuesToSnapshot(getValues(), originalSnapshot, allocationDimensions, dependencyFields);
    };

    return [updateSnapshot];
}

// TODO: TARGET add unit test
/**
 * Hook to wire Target Group and Targets into form
 * @param targetGroupWithTargets Target Group and Targets
 * @param dependencyFields Schema fields that selected field is depending on
 * @param allocationDimensions Allocation Dimensions
 * @param metricOptions
 * @returns
 */
export function useTargetForm(
    targetGroupWithTargets: TargetGroupWithTargets | undefined,
    allocationDimensions: IAllocationDimension[],
    dependencyFields: ISchemaKey[],
    metricOptions: { label: string; value: MetricsKind }[]
): [UseFormReturn<FormFields>, () => TargetGroupWithTargets] {
    const targetGroupSnapshot = targetGroupWithTargets || makeEmptyTargetGroupWithTargets();
    // Target group is trusted to share the same field name. So it's assumed that ANY target can be used to extract field name
    const targetType =
        targetGroupSnapshot.targets.length > 0
            ? targetGroupSnapshot.targets[0].filters.length === 0
                ? // When Target filters is empty array, it means no filter, so select ASSORTMENT_TARGET_TYPE
                  ASSORTMENT_TARGET_TYPE
                : extractFieldNameFromTarget(targetGroupSnapshot.targets[0])
            : "";

    let defaultMetricValue;
    if (targetGroupSnapshot && targetGroupSnapshot.metric) {
        defaultMetricValue = targetGroupSnapshot.metric;
    } else if (metricOptions.length === 1) {
        defaultMetricValue = metricOptions[0].value;
    }

    const form = useForm<FormFields>({
        defaultValues: {
            [FormFieldName.Name]: targetGroupSnapshot.name ?? "",
            [FormFieldName.Metric]: defaultMetricValue,
            [FormFieldName.Type]: targetType,
            [FormFieldName.Values]:
                targetGroupSnapshot.targets.length > 0
                    ? (extractTargetValuesFromTargets(targetGroupSnapshot.targets) as any)
                    : [],
            [FormFieldName.SchemaFields]: {
                ...(targetGroupSnapshot.targets.length > 0
                    ? extractParentFilterValuesFromTarget(targetGroupSnapshot.targets)
                    : {}),
            },
        },
        mode: "onChange",
    });

    const [updateSnapshotFromForm] = useTargetFormToSnapshot(
        form,
        targetGroupSnapshot,
        allocationDimensions,
        dependencyFields
    );

    return [form, updateSnapshotFromForm];
}
